import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge, Form, Tabs, Tab, Pagination, Row, Col } from 'react-bootstrap';
import { usersAPI, adminAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { MailOutlined, SendOutlined, ReloadOutlined, EyeOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import StackedModal from '../common/StackedModal';

const Modal = StackedModal;

const Messages = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('inbox');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showSendAllModal, setShowSendAllModal] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Пагинация
  const [inboxPagination, setInboxPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [sentPagination, setSentPagination] = useState({ page: 1, limit: 20, total: 0 });
  
  // Фильтры
  const [unreadOnly, setUnreadOnly] = useState(false);
  
  // Форма отправки
  const [composeForm, setComposeForm] = useState({
    toUserId: '',
    subject: '',
    message: '',
  });

  // Форма массовой отправки
  const [sendAllForm, setSendAllForm] = useState({
    subject: '',
    message: '',
    filters: {
      isActive: '',
      role: '',
    },
  });
  const [sendAllResult, setSendAllResult] = useState(null);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, inboxPagination.page, sentPagination.page, unreadOnly, user?.role]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Загружаем количество непрочитанных
      const unreadData = await usersAPI.getUnreadCount?.().catch(() => ({ count: 0 }));
      setUnreadCount(unreadData.unreadCount ?? unreadData.count ?? 0);
      
      // Загружаем сообщения в зависимости от активной вкладки
      if (activeTab === 'inbox') {
        const inboxData = await usersAPI.getInboxMessages({
          page: inboxPagination.page,
          limit: inboxPagination.limit,
          unreadOnly: unreadOnly || undefined,
        });
        
        if (inboxData.messages) {
          setInboxMessages(inboxData.messages);
        } else if (inboxData.notifications) {
          setInboxMessages(inboxData.notifications);
        } else if (Array.isArray(inboxData)) {
          setInboxMessages(inboxData);
        }
        
        if (inboxData.pagination) {
          setInboxPagination(prev => ({
            ...prev,
            total: inboxData.pagination.total || 0,
          }));
        }
      } else if (activeTab === 'sent' && user?.role === 'admin') {
        // Загружаем отправленные только для админов
        const sentData = await usersAPI.getSentMessages({
          page: sentPagination.page,
          limit: sentPagination.limit,
        });
        
        if (sentData.messages) {
          setSentMessages(sentData.messages);
        } else if (Array.isArray(sentData)) {
          setSentMessages(sentData);
        }
        
        if (sentData.pagination) {
          setSentPagination(prev => ({
            ...prev,
            total: sentData.pagination.total || 0,
          }));
        }
      }
      
      // Загружаем упрощенный список пользователей для отправки (если админ)
      if (user?.role === 'admin') {
        const usersData = await adminAPI.getUsersList({ limit: 100 }).catch(() => ({ users: [] }));
        if (usersData.users) {
          setUsers(usersData.users);
        }
      }
    } catch (err) {
      setError('Ошибка при загрузке сообщений: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = async (msg) => {
    try {
      const messageData = await usersAPI.getMessage(msg.id);
      // apiService уже обрабатывает разворачивание ответа (response.data.message || response.data)
      // Поэтому здесь мы просто используем полученные данные.
      // Если мы попытаемся сделать messageData.message || messageData, и у объекта есть поле message (текст),
      // то мы ошибочно сохраним только текст вместо всего объекта.
      setSelectedMessage(messageData);
      setShowMessageModal(true);
      
      // Помечаем как прочитанное, если это входящее сообщение
      if (activeTab === 'inbox' && !msg.is_read) {
        await usersAPI.markMessageAsRead(msg.id);
        loadAllData();
      }
    } catch (err) {
      message.error('Ошибка при загрузке сообщения: ' + getTranslatedError(err));
    }
  };

  const handleSendMessage = async () => {
    try {
      // Для обычных пользователей toUserId может быть пустым, если мы отправляем админу
      // В таком случае устанавливаем специальное значение или оставляем пустым, чтобы бэкенд обработал это
      const messageData = { ...composeForm };
      
      if (user?.role !== 'admin') {
         messageData.toUserId = 'inilaw';
      }

      if (!messageData.toUserId || !messageData.subject || !messageData.message) {
        message.error('Заполните все поля');
        return;
      }
      
      await usersAPI.sendMessage(messageData);
      message.success('Сообщение отправлено');
      setShowComposeModal(false);
      setComposeForm({ toUserId: '', subject: '', message: '' });
      loadAllData();
    } catch (err) {
      message.error('Ошибка при отправке: ' + getTranslatedError(err));
    }
  };

  const handleSendToAll = async () => {
    try {
      if (!sendAllForm.subject || !sendAllForm.message) {
        message.error('Заполните тему и сообщение');
        return;
      }
      
      const filters = {};
      if (sendAllForm.filters.isActive !== '') {
        filters.isActive = sendAllForm.filters.isActive === 'true';
      }
      if (sendAllForm.filters.role !== '') {
        filters.role = sendAllForm.filters.role;
      }
      
      const result = await adminAPI.adminSendToAll({
        subject: sendAllForm.subject,
        message: sendAllForm.message,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      });
      
      setSendAllResult(result);
      message.success(`Сообщение отправлено ${result.successful || 0} пользователям из ${result.total || 0}`);
      if (result.failed > 0) {
        message.warning(`Не удалось отправить ${result.failed} сообщений`);
      }
      loadAllData();
    } catch (err) {
      message.error('Ошибка при массовой отправке: ' + getTranslatedError(err));
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await usersAPI.deleteMessage(messageId);
      message.success('Сообщение удалено');
      loadAllData();
    } catch (err) {
      message.error('Ошибка при удалении: ' + getTranslatedError(err));
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await usersAPI.markMessageAsRead(messageId);
      loadAllData();
    } catch (err) {
      message.error('Ошибка: ' + getTranslatedError(err));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <Card className="mb-2">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ fontSize: '16px' }}>
              <MailOutlined className="me-2" />
              Сообщения
              {unreadCount > 0 && (
                <Badge bg="danger" className="ms-2" style={{ fontSize: '11px' }}>
                  {unreadCount} новых
                </Badge>
              )}
            </h6>
            <div className="d-flex gap-2">
              {user?.role === 'admin' && (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowComposeModal(true)}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    <PlusOutlined /> Написать
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => {
                      setShowSendAllModal(true);
                      setSendAllResult(null);
                    }}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    <SendOutlined /> Всем
                  </Button>
                </>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadAllData}
                disabled={loading}
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <ReloadOutlined /> Обновить
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-2">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-2">
              {error}
            </Alert>
          )}

          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-2">
            <Tab
              eventKey="inbox"
              title={
                <span>
                  Входящие
                  {unreadCount > 0 && (
                    <Badge bg="danger" className="ms-2" style={{ fontSize: '9px' }}>
                      {unreadCount}
                    </Badge>
                  )}
                </span>
              }
            >
              {user?.role !== 'admin' && (
                <div className="mb-3 d-flex justify-content-between align-items-center">
                   <Alert variant="info" className="mb-0 flex-grow-1" style={{ fontSize: '12px' }}>
                     Вы можете писать сообщения только администраторам.
                   </Alert>
                   <Button 
                      variant="primary" 
                      size="sm" 
                      className="ms-3"
                      onClick={() => {
                        setComposeForm(prev => ({ ...prev, toUserId: 'inilaw' }));
                        setShowComposeModal(true);
                      }}
                   >
                     <MailOutlined className="me-1"/> Написать администратору
                   </Button>
                </div>
              )}
              <div className="mb-2">
                <Form.Check
                  type="switch"
                  id="unread-only"
                  label="Только непрочитанные"
                  checked={unreadOnly}
                  onChange={(e) => {
                    setUnreadOnly(e.target.checked);
                    setInboxPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  style={{ fontSize: '13px' }}
                />
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : inboxMessages.length === 0 ? (
                <Alert variant="info" className="mb-0" style={{ fontSize: '12px' }}>
                  Нет сообщений
                </Alert>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th style={{ fontSize: '12px' }}>От</th>
                          <th style={{ fontSize: '12px' }}>Тема</th>
                          <th style={{ fontSize: '12px' }}>Дата</th>
                          <th style={{ fontSize: '12px' }}>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inboxMessages.map((msg) => (
                          <tr key={msg.id} style={{ backgroundColor: !msg.is_read ? '#f0f7ff' : 'white' }}>
                            <td style={{ fontSize: '12px' }}>
                              {msg.from_user?.login || msg.fromUser?.login || msg.sender?.login || msg.from?.login || 'Система'}
                            </td>
                            <td style={{ fontSize: '12px' }}>
                              <strong>{msg.subject || msg.title || 'Без темы'}</strong>
                              {!msg.is_read && (
                                <Badge bg="primary" className="ms-2" style={{ fontSize: '9px' }}>
                                  Новое
                                </Badge>
                              )}
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {formatDate(msg.created_at || msg.createdAt)}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleViewMessage(msg)}
                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                >
                                  <EyeOutlined />
                                </Button>
                                {!msg.is_read && (
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleMarkAsRead(msg.id)}
                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                    title="Пометить как прочитанное"
                                  >
                                    ✓
                                  </Button>
                                )}
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  style={{ fontSize: '10px', padding: '2px 6px' }}
                                >
                                  <DeleteOutlined />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {inboxPagination.total > inboxPagination.limit && (
                    <div className="d-flex justify-content-center mt-2">
                      <Pagination size="sm">
                        <Pagination.Prev
                          disabled={inboxPagination.page === 1}
                          onClick={() => setInboxPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        />
                        <Pagination.Item active>{inboxPagination.page}</Pagination.Item>
                        <Pagination.Next
                          disabled={inboxPagination.page * inboxPagination.limit >= inboxPagination.total}
                          onClick={() => setInboxPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Tab>

            {user?.role === 'admin' && (
              <Tab 
                eventKey="sent" 
                title="Отправленные"
              >
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : sentMessages.length === 0 ? (
                  <Alert variant="info" className="mb-0" style={{ fontSize: '12px' }}>
                    Нет отправленных сообщений
                  </Alert>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th style={{ fontSize: '12px' }}>Кому</th>
                            <th style={{ fontSize: '12px' }}>Тема</th>
                            <th style={{ fontSize: '12px' }}>Дата</th>
                            <th style={{ fontSize: '12px' }}>Статус</th>
                            <th style={{ fontSize: '12px' }}>Действия</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sentMessages.map((msg) => (
                            <tr key={msg.id}>
                              <td style={{ fontSize: '12px' }}>
                                {msg.to_user?.login || msg.toUser?.login || msg.recipient?.login || msg.to?.login || '—'}
                              </td>
                              <td style={{ fontSize: '12px' }}>
                                <strong>{msg.subject || 'Без темы'}</strong>
                              </td>
                              <td style={{ fontSize: '11px' }}>
                                {formatDate(msg.created_at || msg.createdAt)}
                              </td>
                              <td>
                                {msg.is_read ? (
                                  <Badge bg="success" style={{ fontSize: '10px' }}>
                                    Прочитано
                                    {msg.read_at && (
                                      <div style={{ fontSize: '9px', marginTop: '2px' }}>
                                        {formatDate(msg.read_at)}
                                      </div>
                                    )}
                                  </Badge>
                                ) : (
                                  <Badge bg="warning" text="dark" style={{ fontSize: '10px' }}>
                                    Не прочитано
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => handleViewMessage(msg)}
                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                  >
                                    <EyeOutlined />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                  >
                                    <DeleteOutlined />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {sentPagination.total > sentPagination.limit && (
                      <div className="d-flex justify-content-center mt-2">
                        <Pagination size="sm">
                          <Pagination.Prev
                            disabled={sentPagination.page === 1}
                            onClick={() => setSentPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                          />
                          <Pagination.Item active>{sentPagination.page}</Pagination.Item>
                          <Pagination.Next
                            disabled={sentPagination.page * sentPagination.limit >= sentPagination.total}
                            onClick={() => setSentPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                          />
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </Tab>
            )}
          </Tabs>
        </Card.Body>
      </Card>

      {/* Модальное окно просмотра сообщения */}
      <Modal show={showMessageModal} onHide={() => setShowMessageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedMessage?.subject || selectedMessage?.title || 'Сообщение'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage ? (
            <div>
              <div className="mb-3">
                <strong>От:</strong> {selectedMessage.from_user?.login || selectedMessage.fromUser?.login || selectedMessage.sender?.login || selectedMessage.from?.login || 'Система'}
              </div>
              <div className="mb-3">
                <strong>Кому:</strong> {selectedMessage.to_user?.login || selectedMessage.toUser?.login || selectedMessage.recipient?.login || selectedMessage.to?.login || '—'}
              </div>
              <div className="mb-3">
                <strong>Дата:</strong> {formatDate(selectedMessage.created_at || selectedMessage.createdAt)}
              </div>
              {selectedMessage.read_at && (
                <div className="mb-3">
                  <strong>Прочитано:</strong> {formatDate(selectedMessage.read_at)}
                </div>
              )}
              <hr />
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
                {selectedMessage.message || selectedMessage.body || selectedMessage.text || selectedMessage.content || selectedMessage.msg || '—'}
              </div>
            </div>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMessageModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно написания сообщения */}
      <Modal show={showComposeModal} onHide={() => setShowComposeModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Новое сообщение</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Кому</Form.Label>
              {user?.role === 'admin' ? (
                // Если админ - показываем список пользователей
                users.length > 0 ? (
                  <Form.Select
                    value={composeForm.toUserId}
                    onChange={(e) => setComposeForm({ ...composeForm, toUserId: e.target.value })}
                    required
                  >
                    <option value="">Выберите пользователя</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.login} {u.name ? `(${u.name})` : ''} {u.email ? `- ${u.email}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    value={composeForm.toUserId}
                    onChange={(e) => setComposeForm({ ...composeForm, toUserId: e.target.value })}
                    placeholder="ID пользователя"
                    required
                  />
                )
              ) : (
                // Если обычный пользователь - всегда отправляем админу
                <Form.Control
                  type="text"
                  value="Администрация"
                  disabled
                />
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Тема</Form.Label>
              <Form.Control
                type="text"
                value={composeForm.subject}
                onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                placeholder="Тема сообщения"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Сообщение</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={composeForm.message}
                onChange={(e) => setComposeForm({ ...composeForm, message: e.target.value })}
                placeholder="Текст сообщения"
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowComposeModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSendMessage}>
            <SendOutlined /> Отправить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно массовой отправки */}
      <Modal show={showSendAllModal} onHide={() => {
        setShowSendAllModal(false);
        setSendAllResult(null);
        setSendAllForm({ subject: '', message: '', filters: { isActive: '', role: '' } });
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Отправить всем пользователям</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendAllResult ? (
            <div>
              <Alert variant="success" className="mb-3">
                <strong>Отправка завершена!</strong>
                <div className="mt-2">
                  <div>Всего пользователей: <strong>{sendAllResult.total || 0}</strong></div>
                  <div>Успешно отправлено: <strong className="text-success">{sendAllResult.successful || 0}</strong></div>
                  {sendAllResult.failed > 0 && (
                    <div>Ошибок: <strong className="text-danger">{sendAllResult.failed}</strong></div>
                  )}
                </div>
              </Alert>
              {sendAllResult.filters && Object.keys(sendAllResult.filters).length > 0 && (
                <div className="mb-3">
                  <strong>Примененные фильтры:</strong>
                  <ul className="mb-0">
                    {sendAllResult.filters.isActive !== undefined && (
                      <li>Активность: {sendAllResult.filters.isActive ? 'Только активные' : 'Только неактивные'}</li>
                    )}
                    {sendAllResult.filters.role && (
                      <li>Роль: {sendAllResult.filters.role}</li>
                    )}
                  </ul>
                </div>
              )}
              {sendAllResult.results && sendAllResult.results.length > 0 && (
                <div>
                  <strong>Результаты (первые 100):</strong>
                  <div className="table-responsive mt-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th style={{ fontSize: '11px' }}>Пользователь</th>
                          <th style={{ fontSize: '11px' }}>Email</th>
                          <th style={{ fontSize: '11px' }}>Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sendAllResult.results.map((r, idx) => (
                          <tr key={idx}>
                            <td style={{ fontSize: '11px' }}>{r.login || r.userId}</td>
                            <td style={{ fontSize: '11px' }}>{r.email || '—'}</td>
                            <td>
                              {r.success ? (
                                <Badge bg="success" style={{ fontSize: '9px' }}>Успешно</Badge>
                              ) : (
                                <Badge bg="danger" style={{ fontSize: '9px' }}>Ошибка</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Тема</Form.Label>
                <Form.Control
                  type="text"
                  value={sendAllForm.subject}
                  onChange={(e) => setSendAllForm({ ...sendAllForm, subject: e.target.value })}
                  placeholder="Тема сообщения"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Сообщение</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={sendAllForm.message}
                  onChange={(e) => setSendAllForm({ ...sendAllForm, message: e.target.value })}
                  placeholder="Текст сообщения для всех пользователей"
                  required
                />
              </Form.Group>
              <hr />
              <h6 style={{ fontSize: '14px' }}>Фильтры (опционально)</h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '13px' }}>Статус активности</Form.Label>
                    <Form.Select
                      value={sendAllForm.filters.isActive}
                      onChange={(e) => setSendAllForm({
                        ...sendAllForm,
                        filters: { ...sendAllForm.filters, isActive: e.target.value }
                      })}
                    >
                      <option value="">Все пользователи</option>
                      <option value="true">Только активные</option>
                      <option value="false">Только неактивные</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '13px' }}>Роль</Form.Label>
                    <Form.Select
                      value={sendAllForm.filters.role}
                      onChange={(e) => setSendAllForm({
                        ...sendAllForm,
                        filters: { ...sendAllForm.filters, role: e.target.value }
                      })}
                    >
                      <option value="">Все роли</option>
                      <option value="user">Только пользователи</option>
                      <option value="admin">Только администраторы</option>
                      <option value="moderator">Только модераторы</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          {sendAllResult ? (
            <Button variant="primary" onClick={() => {
              setShowSendAllModal(false);
              setSendAllResult(null);
              setSendAllForm({ subject: '', message: '', filters: { isActive: '', role: '' } });
            }}>
              Закрыть
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => {
                setShowSendAllModal(false);
                setSendAllResult(null);
                setSendAllForm({ subject: '', message: '', filters: { isActive: '', role: '' } });
              }}>
                Отмена
              </Button>
              <Button variant="success" onClick={handleSendToAll}>
                <SendOutlined /> Отправить всем
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Messages;

